import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'

const ROUTES = [
    { label: 'Home', to: '/' },
    { label: 'About', to: '/about' },
    { label: 'Projects', to: '/projects' },
    { label: 'Publications', to: '/publications' },
    { label: 'Blog', to: '/blog' },
    { label: 'Images', to: '/images' },
    { label: 'Videos', to: '/videos' },
    { label: 'CV', to: '/CV' },
    { label: 'Contact', to: '/contact' },
    { label: 'Calendar', to: '/calendar' },
    { label: 'Notes (admin)', to: '/notes' },
    { label: 'Login', to: '/login' },
]

/**
 * Cmd/Ctrl+K command palette: keyboard-first navigation and the two
 * navbar toggle actions, filterable and arrow-key navigable.
 */
export default function CommandPalette({ open, onClose, theme, setTheme, isZoomed, setIsZoomed }) {
    const [query, setQuery] = useState('')
    const [activeIndex, setActiveIndex] = useState(0)
    const inputRef = useRef(null)
    const listRef = useRef(null)
    const navigate = useNavigate()

    const actions = useMemo(() => ([
        ...ROUTES.map((r) => ({ id: `go-${r.to}`, label: `Go to ${r.label}`, run: () => navigate(r.to) })),
        {
            id: 'toggle-theme',
            label: theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode',
            run: () => setTheme(theme === 'dark' ? 'light' : 'dark'),
        },
        {
            id: 'toggle-zoom',
            label: isZoomed ? 'Decrease text size' : 'Increase text size',
            run: () => setIsZoomed(!isZoomed),
        },
    ]), [navigate, theme, setTheme, isZoomed, setIsZoomed])

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase()
        if (!q) return actions
        return actions.filter((a) => a.label.toLowerCase().includes(q))
    }, [actions, query])

    useEffect(() => {
        if (!open) return
        setQuery('')
        setActiveIndex(0)
        const t = setTimeout(() => inputRef.current?.focus(), 10)
        return () => clearTimeout(t)
    }, [open])

    useEffect(() => setActiveIndex(0), [query])

    useEffect(() => {
        if (!open) return
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose()
                return
            }
            if (e.key === 'ArrowDown') {
                e.preventDefault()
                setActiveIndex((i) => Math.min(i + 1, filtered.length - 1))
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault()
                setActiveIndex((i) => Math.max(i - 1, 0))
            }
            if (e.key === 'Enter') {
                e.preventDefault()
                const item = filtered[activeIndex]
                if (item) {
                    item.run()
                    onClose()
                }
            }
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [open, filtered, activeIndex, onClose])

    return createPortal(
        <AnimatePresence>
            {open && (
                <motion.div
                    className="fixed inset-0 z-[60] flex items-start justify-center bg-black/70 p-4 pt-[12vh]"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.1 }}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) onClose()
                    }}
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.98, y: 6 }}
                        transition={{ type: 'spring', stiffness: 460, damping: 30, mass: 0.7 }}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Command palette"
                        className="w-full max-w-lg border-2 border-black bg-white shadow-brutal-lg dark:border-white dark:bg-zinc-950"
                    >
                        <div className="border-b-2 border-black dark:border-white p-3">
                            <input
                                ref={inputRef}
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Type a command or search."
                                className="w-full bg-transparent font-mono text-sm text-black dark:text-white placeholder:text-zinc-500 focus:outline-none"
                                aria-label="Command palette input"
                            />
                        </div>
                        <ul ref={listRef} role="listbox" className="max-h-80 overflow-y-auto">
                            {filtered.length === 0 && (
                                <li className="px-4 py-6 text-center text-sm text-zinc-500">No matches.</li>
                            )}
                            {filtered.map((item, i) => (
                                <li key={item.id} role="option" aria-selected={i === activeIndex}>
                                    <button
                                        onMouseEnter={() => setActiveIndex(i)}
                                        onClick={() => {
                                            item.run()
                                            onClose()
                                        }}
                                        className={`w-full text-left px-4 py-2.5 font-mono text-sm transition-colors ${i === activeIndex
                                                ? 'bg-accent text-black'
                                                : 'text-black dark:text-white'
                                            }`}
                                    >
                                        {item.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                        <div className="flex items-center justify-between border-t-2 border-black dark:border-white px-4 py-2 text-[11px] text-zinc-500 font-mono">
                            <span>↑↓ navigate</span>
                            <span>Enter select</span>
                            <span>Esc close</span>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>,
        document.body
    )
}
